import dayjs, { Dayjs } from "dayjs";
import { DateValue } from "@mantine/dates";

export const formatDate = (date: DateValue): string => {
    return dayjs(date).format("DD MMM YYYY");
};

/**
 * Safely converts a DateValue to a Date object, ensuring it's parsed as local time.
 * This prevents timezone-related off-by-one-day issues when parsing date strings.
 * @param {DateValue} dateValue - The date value to convert (can be Date, string, or null)
 * @returns {Date | null} The Date object or null if input is null/undefined
 */
export const toLocalDate = (dateValue: DateValue): Date | null => {
    if (!dateValue) return null;

    // If it's already a Date object, return a copy
    if (dateValue instanceof Date) {
        return new Date(dateValue);
    }

    // If it's a string, parse it explicitly as local time using dayjs
    if (typeof dateValue === "string") {
        return dayjs(dateValue).toDate();
    }

    return null;
};

/**
 * Calculates the minimum date based on the provided parameters.
 * @param {number} limitPastDays - The number of days to limit the past.
 * @param {number} limitSelectionMonths - The number of months to limit the selection.
 * @param {Dayjs | null} rangeStart - The start date of the range.
 * @param {Dayjs} now - The current date.
 * @returns {Date | undefined} The minimum date.
 */
export const getMinDate = ({
    limitPastDays,
    limitSelectionMonths,
    rangeStart,
    now = dayjs(),
}: {
    limitPastDays?: number;
    limitSelectionMonths?: number;
    rangeStart?: Dayjs | null;
    now?: Dayjs;
}): Date | undefined => {
    // 1. Calculate the absolute limit based on `limitPastDays` (always applies if defined)
    const absoluteMinDate = limitPastDays ? now.subtract(limitPastDays, "day") : null;

    // 2. If no range is currently being selected (no start date),
    //    the only limit is the absolute one (if it exists).
    if (!rangeStart) {
        return absoluteMinDate?.toDate();
    }

    // 3. If a range is started, we might also have a relative limit based on `limitSelectionMonths`.
    //    This limits how far BACK from the standard start date one can go.
    //    NOTE: Typically 'limitSelectionMonths' limits the *duration*, so it usually affects maxDate.
    //    However, if the user picks the *end* date first (uncommon but possible in some UX), or picks a 'middle' date,
    //    this logic effectively creates a window around the first selection.
    //    Based on requirements: "every dates older or later than 1 months from the first selected date are disabled"
    const relativeMinDate = limitSelectionMonths ? rangeStart.subtract(limitSelectionMonths, "month") : null;

    // 4. Return the most restrictive (latest) date between the two limits.
    if (absoluteMinDate && relativeMinDate) {
        return absoluteMinDate.isAfter(relativeMinDate) ? absoluteMinDate.toDate() : relativeMinDate.toDate();
    }

    return (absoluteMinDate || relativeMinDate)?.toDate();
};

/**
 * Calculates the maximum date based on the provided parameters.
 * @param {Dayjs} maxDate - The maximum date.
 * @param {number} limitSelectionMonths - The number of months to limit the selection.
 * @param {Dayjs | null} rangeStart - The start date of the range.
 * @returns {Date | undefined} The maximum date.
 */
export const getMaxDate = ({
    maxDate,
    limitSelectionMonths,
    rangeStart,
}: {
    maxDate: Dayjs;
    limitSelectionMonths?: number;
    rangeStart?: Dayjs | null;
}): Date | undefined => {
    // 1. The absolute max limit is always the provided `maxDate` (defaults to today in component).
    const absoluteMaxDate = maxDate;

    // 2. If no range is currently being selected, the only limit is the absolute one.
    if (!rangeStart) {
        return absoluteMaxDate.toDate();
    }

    // 3. If a range is started, apply the relative window limit forward.
    const relativeMaxDate = limitSelectionMonths ? rangeStart.add(limitSelectionMonths, "month") : null;

    // 4. Return the most restrictive (earliest) date between the two limits.
    if (relativeMaxDate) {
        return absoluteMaxDate.isBefore(relativeMaxDate) ? absoluteMaxDate.toDate() : relativeMaxDate.toDate();
    }

    return absoluteMaxDate.toDate();
};

export const getFutureClosestDate = (...args: (Dayjs | null)[]) => {
    return args.reduce((prev, current) => (prev?.isBefore(current) || current === null ? prev : current));
};
